import React, { FC, useState } from 'react';
import { Connect, PropsFromRedux } from './CategoriesContainer';
import {
  checkIfCategoryIsSelected,
  MINIMUM_CATS_SHOWN_COUNT,
} from './categoriesUtils';
import { CategoriesNode } from './CategoriesNode';
import { doctrineAnalytics } from '../../../../utils/analyticsUtils';
import { CategoryEnum, CategoryType } from '../../../search.types';
import { getPathFormated } from './categories.utils';
import { useRestrictedModal } from '../../../../components/modal/useRestrictedModal';
import styles from './categories.module.scss';
import { useUser } from '../../../../user/useUser';

type OwnProps = {
  type: CategoryEnum;
  displayedItemCount?: number;
};

type Props = PropsFromRedux & OwnProps;

export const RawCategories: FC<Props> = ({
  aggregationsPaths,
  toggleAggregation,
  toggleAggregationTree,
  searchType,
  type,
  categories,
  displayedItemCount = MINIMUM_CATS_SHOWN_COUNT,
  selectedPanes,
}) => {
  const [panes, setPanes] = useState<{ [s: string]: boolean }>({});
  const { openRestrictedModal } = useRestrictedModal();
  const { accessPremium } = useUser();

  const togglePane = (path: Array<string>) => () => {
    const key = path[path.length - 1];
    const isSelected = panes[key];
    doctrineAnalytics('Search SeeMoreFilterClicked', {
      type: isSelected ? 'see-less' : 'see-more',
      ...getPathFormated(path, searchType),
    });

    setPanes((previous) => ({
      ...previous,
      [key]: !previous[key],
    }));
  };

  const onSelect = (subPath: Array<string>, category: CategoryType) => () => {
    if (!accessPremium) {
      return openRestrictedModal({ source: 'faceted-item' });
    }

    const path = subPath.concat(category.key);
    const isSelected = !checkIfCategoryIsSelected(path, aggregationsPaths);

    if (isSelected) {
      doctrineAnalytics(
        'Search FilterCategoryClicked',
        getPathFormated(path, searchType),
      );
    }

    return toggleAggregation(path, isSelected);
  };

  const onExpand = (subPath: Array<string>, category: CategoryType) => () => {
    if (!accessPremium) {
      return openRestrictedModal({ source: 'faceted-item' });
    }

    const path = subPath.concat(category.key);
    const isExpanded = !checkIfCategoryIsSelected(path, selectedPanes);

    if (isExpanded) {
      const event = isExpanded
        ? 'Search FilterCategoryExpanded'
        : 'Search FilterCategoryCollapsed';
      doctrineAnalytics(event, getPathFormated(path, searchType));
    }
    toggleAggregationTree(path, isExpanded);
  };

  return (
    <CategoriesNode
      className={styles.root}
      items={categories}
      path={[type]}
      panes={panes}
      onSelect={onSelect}
      onToggleSeeMore={togglePane}
      aggregationsPaths={aggregationsPaths}
      displayedItemCount={displayedItemCount}
      onExpand={onExpand}
      selectedPanes={selectedPanes}
    >
      {(category) => (
        <CategoriesNode
          className={styles['sub-wrapper']}
          items={category.sub}
          path={[type, category.key]}
          panes={panes}
          onSelect={onSelect}
          onToggleSeeMore={togglePane}
          aggregationsPaths={aggregationsPaths}
          displayedItemCount={displayedItemCount}
          onExpand={onExpand}
          selectedPanes={selectedPanes}
        >
          {(subCategory) => (
            <CategoriesNode
              className={styles['sub-sub-wrapper']}
              items={subCategory.sub}
              path={[type, category.key, subCategory.key]}
              panes={panes}
              onSelect={onSelect}
              onToggleSeeMore={togglePane}
              aggregationsPaths={aggregationsPaths}
              displayedItemCount={displayedItemCount}
              onExpand={onExpand}
              selectedPanes={selectedPanes}
            />
          )}
        </CategoriesNode>
      )}
    </CategoriesNode>
  );
};

export const Categories = Connect(RawCategories);
